/**
 * Navigation URL Synchronization
 * Handles bidirectional sync between navigation state and URL hash
 */

'use client'

import type { StoreApi, UseBoundStore } from 'zustand'

import type { NavigationStore } from './types'

let urlSyncInitialized = false
let prevPathString = ''

/**
 * Initialize URL synchronization for the navigation store
 * This should be called once on the client side
 */
export const initUrlSync = (store: UseBoundStore<StoreApi<NavigationStore>>) => {
  if (typeof window === 'undefined' || urlSyncInitialized) return

  urlSyncInitialized = true
  prevPathString = store.getState().pathString

  // Sync store → URL when pathString changes
  store.subscribe((state) => {
    if (state.pathString !== prevPathString) {
      prevPathString = state.pathString
      const currentHash = window.location.hash.slice(1)
      if (state.pathString !== currentHash) {
        window.history.replaceState(null, '', state.pathString ? `#${state.pathString}` : window.location.pathname)
      }
    }
  })

  // Sync URL → store on initial load (always sync, even with empty hash)
  // This ensures 'root' is expanded by default when landing on the page
  const initialHash = window.location.hash.slice(1)
  store.getState().syncFromUrl(initialHash)

  // Handle browser back/forward
  window.addEventListener('popstate', () => {
    const hash = window.location.hash.slice(1)
    store.getState().syncFromUrl(hash)
  })
}

/**
 * Reset URL sync state (useful for testing)
 */
export const resetUrlSync = () => {
  urlSyncInitialized = false
  prevPathString = ''
}
